import type { Fulfilment, FulfilmentStatus } from '@felix/domain';
import { Button } from '@mantine/core';
import { IconFilePlus, IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import classes from './Purchasing.module.css';
import { formatAmount, formatQuantity } from './requisitions.js';

const TONE: Record<FulfilmentStatus, StatusTone> = {
  not_ordered: 'neutral',
  pending: 'info',
  partial: 'warning',
  complete: 'success',
};

/**
 * "Requested vs Supplied" (ortağın notu #12; ADR 0047 §6): talep satırı ↔ sipariş ↔ teslim
 * (kabul, hasarlı, eksik) ve kalan. Gemide kalan için yeni talep açılır; ofiste salt okunur.
 * İstenen ve ofisin onayladığı iki sütun (ADR 0111 §A): kalan onaylanana göre.
 */
export function FulfilmentTable({
  fulfilment,
  onShortfall,
}: {
  fulfilment: Fulfilment | undefined;
  /** Yalnız gemide ve izinliyse: kalanlarla yeni talep formu. */
  onShortfall?: (() => void) | null;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.fulfilment.${key}`, options);
  if (!fulfilment?.hasOrders) {
    return <EmptyState title={k('noOrders')} description={k('noOrdersHint')} />;
  }
  const q = (value: number) => formatQuantity(value, locale);
  return (
    <>
      {fulfilment.openOrders.length > 0 && fulfilment.hasShortfall ? (
        <p className={classes.notice} data-tone="info" role="status">
          <IconInfoCircle size={16} aria-hidden />{' '}
          {k('openOrders', { orders: fulfilment.openOrders.join(', ') })}
        </p>
      ) : null}
      {/* Geniş karşılaştırma tablosu dar pencerede bilerek kayar (klavyeyle; WCAG 2.1.1) */}
      <section
        className={table.tableWrap}
        data-allow-scroll
        aria-label={k('title')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={`${table.table} ${classes.table}`}>
          <thead>
            <tr>
              <th scope="col" className={classes.index}>
                {t('purchasing.form.columns.no')}
              </th>
              <th scope="col" className={classes.key}>
                {t('purchasing.form.columns.material')}
              </th>
              <th scope="col">{t('purchasing.form.columns.uom')}</th>
              <th scope="col" data-numeric>
                {k('requested')}
              </th>
              <th scope="col" data-numeric>
                {k('approved')}
              </th>
              <th scope="col" data-numeric>
                {k('ordered')}
              </th>
              <th scope="col" data-numeric>
                {k('accepted')}
              </th>
              <th scope="col" data-numeric>
                {k('damaged')}
              </th>
              <th scope="col" data-numeric>
                {k('missing')}
              </th>
              <th scope="col" data-numeric>
                {k('outstanding')}
              </th>
              <th scope="col" data-numeric>
                {k('unitPrice')}
              </th>
              <th scope="col">{k('orders')}</th>
              <th scope="col">{k('status')}</th>
            </tr>
          </thead>
          <tbody>
            {fulfilment.rows.map((row) => {
              // Eski sunucu onaylananı göndermez: talep olduğu gibi onaylıdır
              const approved = row.approved ?? row.requested;
              const added = row.requisitionLineId === null && approved > 0;
              return (
                <tr key={row.key}>
                  <td className={classes.index}>{row.lineNo ?? '—'}</td>
                  <td className={`${classes.material} ${classes.key}`}>
                    {row.name}
                    {row.substitute ? (
                      // İkame istenen kalemi karşılamaz (ADR 0115)
                      <small className={classes.pasteNote}>
                        {t('purchasingShip.fulfilment.substituteFor', {
                          name: row.substituteFor ?? '—',
                        })}
                      </small>
                    ) : row.requisitionLineId === null ? (
                      <small className={classes.pasteNote}>
                        {added ? t('purchasing.review.added') : k('notRequested')}
                      </small>
                    ) : null}
                  </td>
                  <td>{row.unit}</td>
                  <td data-numeric>{q(row.requested)}</td>
                  <td data-numeric data-tone={approved !== row.requested ? 'warning' : undefined}>
                    {approved !== row.requested ? <strong>{q(approved)}</strong> : q(approved)}
                  </td>
                  <td data-numeric>{q(row.ordered)}</td>
                  <td data-numeric>{q(row.accepted)}</td>
                  <td data-numeric data-tone={row.damaged > 0 ? 'danger' : undefined}>
                    {q(row.damaged)}
                  </td>
                  <td data-numeric>
                    {row.missing > 0 ? <strong>{q(row.missing)}</strong> : q(row.missing)}
                  </td>
                  <td data-numeric>
                    <strong>{q(row.outstanding)}</strong>
                  </td>
                  <td data-numeric>
                    {row.unitPrice === null
                      ? '—'
                      : `${formatAmount(row.unitPrice, locale)} ${row.currency ?? ''}`}
                  </td>
                  <td>{row.orders.length > 0 ? row.orders.join(', ') : '—'}</td>
                  <td>
                    <StatusBadge tone={TONE[row.status]}>{k(`statuses.${row.status}`)}</StatusBadge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      {onShortfall && fulfilment.hasShortfall ? (
        <div>
          <Button variant="light" leftSection={<IconFilePlus size={16} />} onClick={onShortfall}>
            {k('createShortfall')}
          </Button>
        </div>
      ) : null}
    </>
  );
}
